import { QueryClient } from "@tanstack/react-query";

import { LayoutRecord } from "@/shared/domain/layouts";
import { HttpClient } from "@/shared/http/HttpClient";
import { Datasource, State } from "@/shared/state/visualization";

/**
 * Context required to initialize the visualizer.
 */
export interface VisualizerInitContext {
  orgId: string;

  /** Authenticated client the resolvers construct their domain services from. */
  httpClient: HttpClient;

  /**
   * Cache the resolvers fetch through, so the route's components find their
   * queries already resolved.
   */
  queryClient: QueryClient;

  /** Derive the visualizer's scope (datasources and panels) from an event */
  eventId?: string;

  /** Initialize the visualizer from a layout definition provided in the navigation state */
  layoutFromNavState?: {
    layoutDefinition: unknown;
    schemaVersion: number;
  };

  /** Initialize the visualizer from a layout */
  layoutName?: string;

  /** Initialize the visualizer from a shared workspace link */
  workspaceId?: string;
}

/**
 * `name` of the recoverable error the layout resolver emits when several
 * layouts share the requested name. The workspace matches on it to open the
 * layout-conflict modal instead of showing an error notification.
 */
export const LAYOUT_CONFLICT_ERROR_NAME = "LayoutConflictError";

/**
 * Where the view builds its initial panels from. Exactly one source wins,
 * chosen by priority; `.type` carries that provenance for the hooks that
 * hydrate a layout or sync a shared workspace's URL.
 */
export type ConfigSource =
  /** A saved workspace, restored whole. */
  | { type: "workspace"; value: State }
  /**
   * A layout hydrated into panels. `origin` records which resolver produced
   * it, so consumers can tell a layout the user asked for (a `?layout=` name,
   * a handoff through navigation state) from one derived on their behalf
   * (the event's scope, their default layout).
   */
  | {
      type: "layout";
      value: LayoutRecord;
      origin: "layout-name" | "nav-state" | "event" | "default";
    }
  /** A layout condition the view surfaces without tearing down: a name that
   * matches nothing, several layouts sharing a name, or a deleted default. */
  | {
      type: "recoverable-error";
      value: {
        /**
         * Identifies the notification; the workspace special-cases
         * {@link LAYOUT_CONFLICT_ERROR_NAME}.
         */
        name: string;
        /** Headline shown above the error message. */
        title?: string;
        /** User-facing error message. */
        message: string;
        /**
         * Data an error-specific handler needs, e.g. the conflicting
         * layouts for the layout-conflict modal.
         */
        metadata?: Record<string, unknown>;
      };
    }
  /** Nothing resolved: open with empty panels, loading datasources only. */
  | null;

/** Which sidebar view the `?event=` link opens. */
export interface EventSidebar {
  eventId: string;
  /**
   * "details" when the event loaded, "events-list" when it could not, and
   * "none" when a restored workspace dropped the event — the sidebar stays
   * closed.
   */
  view: "details" | "events-list" | "none";
}

/** A banner the workspace raises once, on load. */
export interface VisualizerNotice {
  /** Identifies the banner; describes the outcome it reports. */
  name: string;
  /**
   * Headline shown above the message.
   */
  title?: string;
  /** User-facing message. */
  message: string;
  severity: "error" | "info";
}

/**
 * Everything the /visualize route needs to open a workspace: the source of
 * its initial panels, the datasources to load, which sidebar view the linked
 * event opens, and any banners to raise.
 */
export interface VisualizerInitData {
  /** The saved workspace or layout the view builds its initial panels from; null when neither resolved. */
  configSource: ConfigSource;
  /** Datasources to load, merged and deduped from the URL and the linked event; empty when a workspace was restored. */
  datasources: Datasource[];
  /** Which sidebar view the ?event= link opens; absent when the URL named no event. */
  eventSidebar?: EventSidebar;
  /**
   * Banners to raise: event dropped for a workspace, event not found or
   * inaccessible, or no reachable event data.
   */
  notices: VisualizerNotice[];
}
